import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { logAction } from '@/utils/permAuditLogger'

// ─── 路由表 ───────────────────────────────────────────────────────────
// 注: meta.module 用于 V2 权限基础设施全局守卫. 现有路由全部保留 + 仅追加 module 字段.
//     模块代码与 permissionMatrixStore.MODULES 对齐.
const routes: RouteRecordRaw[] = [
  { path: '/login', component: () => import('@/views/Login.vue'), meta: { public: true } },
  { path: '/403',   component: () => import('@/views/Forbidden.vue'), meta: { public: true, title: '无权限访问' } },
  // V169 (UAT-FB#66 P3, 2026-06-17): 候选人入职门户 H5 — 无需登录, token 鉴权
  // 兼容短信/微信把 token 放到 query 的历史链接；无 token 时由页面给出明确链接错误。
  { path: '/portal/onboarding', component: () => import('@/views/portal/onboarding/Index.vue'), meta: { public: true, title: '入职门户' } },
  { path: '/portal/onboarding/:token', component: () => import('@/views/portal/onboarding/Index.vue'), meta: { public: true, title: '入职门户' } },
  // 2026-06-21: 候选人「我的招聘进度」H5 — 无需登录, token 鉴权; 只读大阶段, 隐私安全
  { path: '/portal/progress/:token', component: () => import('@/views/portal/recruit-progress/Index.vue'), meta: { public: true, title: '我的招聘进度' } },
  // V173 (UAT-FB#80, 2026-06-17): 普工扫码 H5 — 无需登录, token 鉴权
  { path: '/portal/worker-onboard/:token', component: () => import('@/views/portal/worker-onboard/Index.vue'), meta: { public: true, title: '普工入职登记' } },
  // V165: 候选人扫码投递 H5 — linkCode 鉴权, 无需登录
  { path: '/h5/apply/:code', component: () => import('@/views/portal/candidate-apply/Index.vue'), meta: { public: true, title: '职位申请' } },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    redirect: '/dashboard',
    children: [
      { path: 'dashboard',  name: 'dashboard',  component: () => import('@/views/dashboard/Index.vue'),  meta: { title: '仪表盘',     icon: 'Grid', module: 'dashboard', permission: 'dashboard:read' } },
      { path: 'org',        name: 'org',        component: () => import('@/views/org/Index.vue'),        meta: { title: '组织管理',   icon: 'Share', module: 'org', permission: 'org:read' } },
      { path: 'emp',        name: 'emp',        component: () => import('@/views/emp/Index.vue'),        meta: { title: '员工管理',   icon: 'User', module: 'emp', permission: 'emp:read' } },
      { path: 'contract-seal', name: 'contract-seal', component: () => import('@/views/contract/SealWorkQueue.vue'), meta: { title: '合同用印', icon: 'Stamp', module: 'contract', permission: 'contract:seal:apply' } },
      { path: 'att',        name: 'att',        component: () => import('@/views/att/Index.vue'),        meta: { title: '假勤管理',   icon: 'Calendar', module: 'att', permission: 'att:read' } },
      { path: 'pay',        name: 'pay',        component: () => import('@/views/pay/Index.vue'),        meta: { title: '薪酬管理',   icon: 'Wallet', module: 'pay', permission: 'pay:read' } },
      { path: 'social',     name: 'social',     component: () => import('@/views/social/Index.vue'),     meta: { title: '社保公积金', icon: 'Lock', module: 'social', permission: 'social:read' } },
      { path: 'perf',       name: 'perf',       component: () => import('@/views/perf/Index.vue'),       meta: { title: '绩效管理',   icon: 'Aim', module: 'perf', permission: 'perf:read' } },
      { path: 'rec',        name: 'rec',        component: () => import('@/views/rec/Index.vue'),        meta: { title: '招聘管理',   icon: 'Search', module: 'rec', permission: 'rec:read' } },
      { path: 'interview',  name: 'interview',  component: () => import('@/views/interview/Index.vue'),  meta: { title: '面试资源中心', icon: 'Calendar', module: 'interview', permission: 'interview:read' } },
      // 招聘推荐已并入招聘管理（第 5 Tab），保留 /referral 路由 redirect 用于外部链接
      { path: 'referral',   redirect: { path: '/rec', query: { tab: 'referral' } } },
      { path: 'lrn',        name: 'lrn',        component: () => import('@/views/lrn/Index.vue'),        meta: { title: '学习平台',   icon: 'Notebook', module: 'lrn', permission: 'lrn:read' } },
      { path: 'proc',       name: 'proc',       component: () => import('@/views/proc/Index.vue'),       meta: { title: '流程管理',   icon: 'Document', module: 'proc', permission: 'proc:read' } },
      { path: 'notify',     name: 'notify',     component: () => import('@/views/notify/Index.vue'),     meta: { title: '通知中心',   icon: 'Bell', module: 'notify', permission: 'notify:read' } },
      { path: 'data',       name: 'data',       component: () => import('@/views/data/Index.vue'),       meta: { title: '数据分析',   icon: 'TrendCharts', module: 'data', permission: 'data:read' } },
      { path: 'md',         name: 'md',         component: () => import('@/views/md/Index.vue'),         meta: { title: 'HR 主数据',  icon: 'Files', module: 'md', permission: 'md:read' } },
      { path: 'roles',      name: 'roles',      component: () => import('@/views/roles/Index.vue'),      meta: { title: '角色与权限', icon: 'UserFilled', module: 'roles', permission: 'roles:read' } },
      { path: 'mobile-entry', name: 'mobile', redirect: '/mobile', meta: { title: '移动端入口', icon: 'Iphone', module: 'mobile', permission: 'mobile:read' } },
      { path: 'integration',name: 'integration',component: () => import('@/views/integration/Index.vue'),meta: { title: '系统集成',   icon: 'Link', module: 'integration', permission: 'integration:read' } },
      { path: 'settings',   name: 'settings',   component: () => import('@/views/settings/Index.vue'),   meta: { title: '系统设置',   icon: 'Setting', module: 'settings', permission: 'settings:read' } },
    ],
  },
  {
    path: '/mobile',
    name: 'mobile-app-entry',
    component: () => import('@/views/mobile/Index.vue'),
    meta: { title: '移动端入口', module: 'mobile', permission: 'mobile:read' },
  },
  {
    path: '/me',
    component: () => import('@/layouts/MobileLayout.vue'),
    redirect: '/me/home',
    children: [
      { path: 'home', name: 'me-home', component: () => import('@/views/me/MeHome.vue'), meta: { title: '工作台', module: 'me', requireEmpId: true } },
      { path: 'punch', name: 'me-punch', component: () => import('@/views/me/PunchPage.vue'), meta: { title: '上下班打卡', module: 'me', requireEmpId: true } },
      { path: 'schedule', name: 'me-schedule', component: () => import('@/views/me/MeSchedulePage.vue'), meta: { title: '我的排班', module: 'me', requireEmpId: true, mobileNav: false } },
      { path: 'attendance-monthly', name: 'me-attendance-monthly', component: () => import('@/views/me/MeAttendanceMonthlyPage.vue'), meta: { title: '考勤月报', module: 'me', requireEmpId: true, mobileNav: false } },
      { path: 'leave', name: 'me-leave', component: () => import('@/views/me/MeLeavePage.vue'), meta: { title: '我的假勤', module: 'me', requireEmpId: true } },
      { path: 'payslip', name: 'me-payslip', component: () => import('@/views/me/MePayslipPage.vue'), meta: { title: '我的工资', module: 'me', requireEmpId: true } },
      { path: 'profile', name: 'me-profile', component: () => import('@/views/me/MeProfilePage.vue'), meta: { title: '个人中心', module: 'me', requireEmpId: true } },
      { path: 'employee-profile', name: 'me-employee-profile', component: () => import('@/views/me/MeEmployeeProfilePage.vue'), meta: { title: '员工档案', module: 'me', requireEmpId: true, mobileNav: false } },
      { path: 'onboarding', name: 'me-onboarding', component: () => import('@/views/me/MeOnboardingPage.vue'), meta: { title: '入职进度', module: 'me', requireEmpId: true, mobileNav: false } },
      { path: 'social', name: 'me-social', component: () => import('@/views/me/MeSocialPage.vue'), meta: { title: '社保公积金', module: 'me', requireEmpId: true, mobileNav: false } },
      { path: 'directory', name: 'me-directory', component: () => import('@/views/me/MeDirectoryPage.vue'), meta: { title: '组织通讯录', module: 'me', requireEmpId: true, mobileNav: false } },
      { path: 'notifications', name: 'me-notifications', component: () => import('@/views/me/MeNotificationsPage.vue'), meta: { title: '通知中心', module: 'me', requireEmpId: true, mobileNav: false } },
      { path: 'approvals', name: 'me-approvals', component: () => import('@/views/me/MeApprovalsPage.vue'), meta: { title: '待我审批', module: 'me', mobileNav: false } },
      { path: 'processes', name: 'me-processes', component: () => import('@/views/proc/ProcMyAttemptsPanel.vue'), meta: { title: '我的流程', module: 'me', requireEmpId: true, mobileNav: false } },
      { path: 'probation', name: 'me-probation', component: () => import('@/views/probation/MyProbationCenter.vue'), meta: { title: '我的试用期', module: 'me', requireEmpId: true, mobileNav: false } },
      { path: 'contracts', name: 'me-contracts', component: () => import('@/views/me/MeContractsPage.vue'), meta: { title: '我的合同', module: 'me', requireEmpId: true, mobileNav: false } },
    ],
  },
  { path: '/portal/my-probation', redirect: '/me/probation' },
  { path: '/:pathMatch(.*)*', redirect: '/dashboard' },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// 路由权限默认拒绝：公开路由之外必须具备有效会话；管理页面还必须具备明确的 read 权限。
router.beforeEach(async (to) => {
  if (to.meta.public) return true

  const userStore = useUserStore()
  if (!userStore.token) return { path: '/login', query: { redirect: to.fullPath } }

  // 页面刷新时 Pinia 可能只有 token，先从 /auth/me 恢复权限上下文；恢复失败一律回登录页。
  const currentUser = await userStore.getCurrentUser()
  if (!currentUser) return { path: '/login', query: { redirect: to.fullPath } }

  if (to.meta?.requireEmpId && !currentUser.empId) {
    console.warn('[router] 当前账号未绑定员工档案, 不可访问', to.path)
    return { path: '/403', query: { reason: 'no-emp-id', from: to.fullPath } }
  }

  const requiredPermission = to.meta?.permission as string | undefined
  const moduleCode = to.meta?.module as string | undefined
  const routePermission = moduleCode === 'md' && ['legal', 'contracttpl'].includes(String(to.query.tab || ''))
    ? 'emp:contract:template'
    : moduleCode === 'md' && to.query.tab === 'probationTpl'
      ? 'emp:update'
      : requiredPermission
  if (routePermission && !currentUser.perms?.includes(routePermission)) {
    try {
      logAction({
        module: moduleCode || routePermission.split(':')[0],
        action: 'READ',
        target: { type: 'Route', id: to.path, summary: String(to.meta?.title || moduleCode || requiredPermission) },
        status: 'DENIED',
        errorMessage: `路由守卫: 缺少权限 ${routePermission}`,
      })
    } catch { /* 审计日志失败不能改变拒绝结果 */ }
    return {
      path: '/403',
      query: {
        reason: 'permission-denied',
        from: to.fullPath,
        required: routePermission,
      },
    }
  }

  return true
})

export default router
